import { ReactNode, useCallback, useId } from "react";

import BadgeButton from "./BadgeButton";
import Card from "./card";
import { ModalPortal } from "./modal/modal-portal";
import Typography from "./typography";

export type ConfirmationTone = "primary" | "danger";

export interface ConfirmationModalProps {
  /** Controls whether the modal is visible. */
  isOpen: boolean;
  /** Accessible title displayed in the header. */
  title: string;
  /** Primary message explaining the action. */
  message?: ReactNode;
  /** Optional additional body content rendered below the main message. */
  children?: ReactNode;
  /** Text for the confirm action. */
  confirmText?: string;
  /** Text for the cancel action. */
  cancelText?: string;
  /** Visual tone for the confirm button and header accents. */
  tone?: ConfirmationTone;
  /** Whether the confirm button should show a busy state. */
  isConfirming?: boolean;
  /**
   * When true (default), the modal invokes `onClose` after `onConfirm` resolves
   * synchronously. Toggle off if the parent controls dismissal manually.
   */
  shouldCloseOnConfirm?: boolean;
  /** Triggered when the confirm button is pressed. */
  onConfirm: () => void;
  /** Triggered when the modal should close, including cancel and overlay clicks. */
  onClose: () => void;
}

const toneStyles: Record<
  ConfirmationTone,
  { header: string; confirmTone: "primary" | "danger" }
> = {
  primary: {
    header: "text-terracotta-700",
    confirmTone: "primary",
  },
  danger: {
    header: "text-danger",
    confirmTone: "danger",
  },
};

/**
 * Branded confirmation dialog that composes the shared modal primitives with
 * Harper's Terracotta-forward palette.
 */
export function ConfirmationModal({
  isOpen,
  title,
  message,
  children,
  confirmText = "Confirm",
  cancelText = "Cancel",
  tone = "primary",
  isConfirming = false,
  shouldCloseOnConfirm = true,
  onConfirm,
  onClose,
}: ConfirmationModalProps) {
  const titleId = useId();
  const descriptionId = useId();

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    onConfirm();
    if (shouldCloseOnConfirm) {
      onClose();
    }
  }, [onConfirm, onClose, shouldCloseOnConfirm]);

  if (!isOpen) {
    return null;
  }

  const { header, confirmTone } = toneStyles[tone];

  return (
    <ModalPortal isOpen={isOpen} onClose={handleCancel}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={message ? descriptionId : undefined}
      >
        <Card
          variant="cream"
          elevation={3}
          className="w-full max-w-lg overflow-hidden"
        >
          <Card.Header variant="cream" divider className="bg-cream-100">
            <Typography variant="h4" className={header}>
              <span id={titleId}>{title}</span>
            </Typography>
          </Card.Header>
          <Card.Body className="space-y-4 bg-white">
            {message ? (
              <Typography variant="body2" className="text-brown-700">
                <span id={descriptionId}>{message}</span>
              </Typography>
            ) : null}
            {children}
          </Card.Body>
          <Card.Footer
            variant="cream"
            className="flex flex-col gap-3 bg-cream-50 sm:flex-row sm:justify-end"
          >
            <BadgeButton
              tone="neutral"
              variant="outline"
              onClick={handleCancel}
              type="button"
            >
              {cancelText}
            </BadgeButton>
            <BadgeButton
              tone={confirmTone}
              variant="solid"
              onClick={handleConfirm}
              disabled={isConfirming}
              aria-busy={isConfirming}
              type="button"
            >
              {isConfirming ? "Processing..." : confirmText}
            </BadgeButton>
          </Card.Footer>
        </Card>
      </div>
    </ModalPortal>
  );
}

export default ConfirmationModal;
